/**
 * Single source of truth for marketplace display strings + price formatting.
 *
 * Lives in the package (not in `apps/web`) because admin tables, browse
 * cards, detail page, sell-review step, and listings dashboard all need
 * the same labels. Previously each call site re-derived these and drifted.
 */

import type { ListingCondition } from './types';

/**
 * Maps a listing condition to its i18n key. The service/package layer must
 * not embed display English — the app ships in 15 languages — so call sites
 * resolve the key with their translator (`t(...)` / `<Trans i18nKey=.../>`).
 * Single source of truth so the sell-review step, browse card, detail page,
 * and active-filter chips all stay in sync.
 */
export const CONDITION_LABEL_KEY: Record<ListingCondition, string> = {
  new: 'explore:activeFilters.conditionNew',
  like_new: 'explore:activeFilters.conditionLikeNew',
  good: 'explore:activeFilters.conditionGood',
  fair: 'explore:activeFilters.conditionFair',
};

/**
 * Default display locale for marketplace prices.
 *
 * Locked decision A2 (WIP_marketplace_master_strategy_2026_05_16.md, Phase 1):
 *   EUR-only display by default to serve Phili's primary DACH audience. The
 *   underlying listing currency is whatever the seller chose at publish time
 *   — this locale only governs number formatting (decimal separator, symbol
 *   position, grouping). Phase 1.5 layers FX conversion on top.
 *
 * 'en-IE' (Irish English) puts the currency symbol on the left with comma
 * thousands separators, which reads cleanly for EUR (€5,000), CHF
 * (CHF 5,000), GBP (£5,000), and USD ($5,000). The previous 'en-CH' put
 * the currency code on the LEFT for CHF but suffix-positioned for EUR,
 * which Phili flagged as a UX inconsistency.
 */
const DEFAULT_LOCALE = 'en-IE';

/**
 * Format a marketplace price stored as integer cents.
 *
 * @param cents     - integer minor units of `currency`
 * @param currency  - ISO 4217 code (e.g. 'EUR', 'CHF', 'GBP', 'USD'). Comes
 *                    from the listing row's `currency` column; the seller's
 *                    chosen denomination — NOT converted.
 * @param locale    - optional BCP 47 locale (defaults to 'en-IE' which is
 *                    EUR-friendly). Callers in localized contexts (a
 *                    French-speaking buyer page in Phase 1.5) can pass
 *                    'fr-FR' etc.
 *
 * Wraps `Intl.NumberFormat` directly rather than `apps/web/lib/formatters`
 * `formatCurrency` so the marketplace package has zero app-layer
 * dependencies (the package is consumed by both web and mobile).
 */
export function formatListingPrice(
  cents: number,
  currency: string,
  locale: string = DEFAULT_LOCALE,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Round a converted price to the nearest conventional increment for the
 * target currency. Avoids showing "approximately CHF 47.38" (ugly).
 *
 * CHF/EUR → nearest 0.50, USD/GBP → nearest 1.00.
 */
function roundToConvention(cents: number, currency: string): number {
  const upper = currency.toUpperCase();
  if (upper === 'CHF' || upper === 'EUR') {
    return Math.round(cents / 50) * 50;
  }
  return Math.round(cents / 100) * 100;
}

/**
 * Format a listing price with optional cross-currency conversion.
 *
 * When `viewerCurrency` differs from `sellerCurrency`, prepends "~" to
 * signal the figure is approximate (FX-cache-based, not Stripe-live).
 * When they match, returns the exact formatted price.
 *
 * @param sellerCents     - price in seller's currency (minor units)
 * @param sellerCurrency  - ISO 4217 code the seller priced in
 * @param viewerCurrency  - ISO 4217 code the viewer should see
 * @param fxRate          - conversion rate (sellerCurrency → viewerCurrency).
 *                          Pass 1 or omit when currencies match.
 * @param locale          - BCP 47 locale for number formatting
 */
export function formatConvertedPrice(
  sellerCents: number,
  sellerCurrency: string,
  viewerCurrency: string,
  fxRate: number = 1,
  locale: string = DEFAULT_LOCALE,
): { formatted: string; isApproximate: boolean } {
  const sameCurrency =
    sellerCurrency.toUpperCase() === viewerCurrency.toUpperCase();

  if (sameCurrency || fxRate === 1) {
    return {
      formatted: formatListingPrice(sellerCents, sellerCurrency, locale),
      isApproximate: false,
    };
  }

  const convertedCents = roundToConvention(
    Math.round(sellerCents * fxRate),
    viewerCurrency,
  );

  const formatted = formatListingPrice(convertedCents, viewerCurrency, locale);

  return {
    formatted: `~ ${formatted}`,
    isApproximate: true,
  };
}
